const baseTheme = {
  fonts: {
    primary: '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
    // Display serif, self-hosted. Reserved for large editorial headlines (the
    // sign-in screen); never for UI chrome, labels, or data.
    display: '"Newsreader", ui-serif, Georgia, "Times New Roman", serif',
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
  },

  fontWeights: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
  },

  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  transitions: {
    default: 'all 0.2s ease-in-out',
    fast: 'all 0.15s ease-out',
    slow: 'all 0.3s ease-in-out',
  },

  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    xxl: '1536px',
  },
};

export const lightTheme = {
  ...baseTheme,
  colors: {
    // Primary brand colors
    primary: '#1B4D3E',
    primaryHover: '#143D32',
    primaryLight: '#2D6A4F',
    primaryGradient: 'linear-gradient(135deg, #1B4D3E 0%, #2D6A4F 100%)',

    // Accent green
    accent: '#40916C',
    accentLight: '#52B788',

    // Status colors
    success: '#2F9E64',
    successHover: '#257F50',
    successLight: '#DDF3E6',
    warning: '#C98A12',
    warningHover: '#A87210',
    warningLight: '#FBF0D6',
    danger: '#D14343',
    dangerHover: '#B33535',
    dangerLight: '#FBE7E7',

    // Surface colors - light mode ("greenhouse neutral", 2026-10-01: neutrals with a faint
    // green cast, so the chrome sits with the crop photos instead of a cold grey)
    background: '#F5F7F6',
    surface: '#FFFFFF',
    surfaceHover: '#EEF2EF',
    surfaceGreen: '#1B4D3E',
    border: '#E3E7E4',
    borderHover: '#D3D9D5',

    // Text colors
    text: '#16201B',
    textSecondary: '#3E4943',
    textMuted: '#66706A',
    textInverse: '#FFFFFF',

    // Status mapping
    normal: '#2F9E64',
    critical: '#D14343',
    offline: '#A9B1AC',

    // Signature accent: grow-light magenta, the colour the farm glows at night.
    // Rationed: chart highlights only (e.g. the Dashboard's power line), never UI chrome.
    signature: '#B8327F',
    sprout: '#2A9D8F', // the establishment stage: newly planted

    // Sensor colors
    ph: '#4361EE',
    temperature: '#F8961E',
    humidity: '#4CC9F0',
    water: '#4895EF',
    // Plant Viewer progress chart: budding / flowering lines (stage hues, validated as a pair).
    chartBudding: '#4895EF',
    chartFlowering: '#B8327F',
    // One hue per system on the Plant Viewer's statistics charts (dataviz categorical slots 1-4,
    // validated on #FFFFFF / #171D1A: CVD ΔE ≥ 9.1 light, 8.4 dark; light aqua/yellow < 3:1, so lines carry end labels).
    seriesRackA: '#2a78d6',
    seriesRackB: '#eb6834',
    seriesRackC: '#1baf7a',
    seriesAero: '#eda100',
    co2: '#7209B7',
    // Light (heat) breaks on the Rooms climate chart: purple, apart from every sensor line there.
    heatBreak: '#7C3AED',
    tds: '#3A86FF',
  },
  shadows: {
    sm: '0 1px 3px 0 rgb(22 32 27 / 0.08)',
    md: '0 4px 6px -1px rgb(22 32 27 / 0.08), 0 2px 4px -2px rgb(22 32 27 / 0.08)',
    lg: '0 10px 15px -3px rgb(22 32 27 / 0.08), 0 4px 6px -4px rgb(22 32 27 / 0.08)',
  },
};

export const darkTheme = {
  ...baseTheme,
  colors: {
    // Primary brand colors
    primary: '#3FA57A',
    primaryHover: '#4CC28A',
    primaryLight: '#2D6A4F',
    primaryGradient: 'linear-gradient(135deg, #2D6A4F 0%, #40916C 100%)',

    // Accent green
    accent: '#52B788',
    accentLight: '#74C69D',

    // Status colors
    success: '#4CC28A',
    successHover: '#3FA57A',
    successLight: '#183A2A',
    warning: '#E0A93A',
    warningHover: '#EDBE5E',
    warningLight: '#3A2F17',
    danger: '#E26464',
    dangerHover: '#EA8080',
    dangerLight: '#3A1E1E',

    // Surface colors - dark mode
    background: '#0F1412',
    surface: '#171D1A',
    surfaceHover: '#212924',
    surfaceGreen: '#1B4D3E',
    border: '#26302B',
    borderHover: '#34403A',

    // Text colors
    text: '#E6EBE8',
    textSecondary: '#B4BDB8',
    // #9AA39E measures ~6:1 on surface; keep small labels at or above WCAG AA.
    textMuted: '#9AA39E',
    textInverse: '#0F1412',

    // Status mapping
    normal: '#4CC28A',
    critical: '#E26464',
    offline: '#5C6660',

    // Signature accent: grow-light magenta, the colour the farm glows at night.
    // Rationed: chart highlights only (e.g. the Dashboard's power line), never UI chrome.
    signature: '#E063AE',
    sprout: '#4FC1B3',

    // Sensor colors
    ph: '#6B8AFF',
    temperature: '#FFB347',
    humidity: '#6DD5FA',
    water: '#74B9FF',
    // Dark steps of the same hues, inside the chart lightness band on the dark surface.
    chartBudding: '#4C95E8',
    chartFlowering: '#D85AA3',
    seriesRackA: '#3987e5',
    seriesRackB: '#d95926',
    seriesRackC: '#199e70',
    seriesAero: '#c98500',
    co2: '#A855F7',
    heatBreak: '#A78BFA',
    tds: '#60A5FA',
  },
  shadows: {
    sm: '0 1px 3px 0 rgb(0 0 0 / 0.3)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.3), 0 4px 6px -4px rgb(0 0 0 / 0.3)',
  },
};

// Default export for backwards compatibility
export default lightTheme;
